import { useCallback, useEffect, useState } from "react";
import { HouseAPI } from "../../../API/houseApi";
import {
  IWizardHouseState,
  T_WIZARD_HOUSE_STEP,
  T_WIZARD_HOUSE_STEPS_PATH,
  TWizardHouseType,
  WIZARD_HOUSE_STEPS,
  WIZARD_HOUSE_STEPS_PATHS
} from "../interfaces";
import { Wizard } from "../index";
import {
  WIZARD_HOUSE_DEFAULT_STATE,
  WIZARD_HOUSE_DESCRIPTION_STEPS_CONFIGURATION,
  WIZARD_HOUSE_DESCRIPTION_STEPS_ORDER,
  WIZARD_HOUSE_FULL_STEPS_ORDER,
  WIZARD_HOUSE_PUBLISH_STEPS_ORDER
} from "./constants";
import { useContextHospi } from "../../../context/ContextHospi";
import styled from "styled-components/macro";
import { usePromisesLoader } from "../../../utils/usePromiseLoader";
import { LoadingIndicator } from "../../LoadingIndicator";
import { useHospiNavigate } from "../../../utils/common";
import {
  HOST_HOUSE_WIZARD_ROUTE,
  HOST_ROOMS_ROUTE
} from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";
import {
  invalidateHouses,
  invalidateWizardsStateShort
} from "../../../API/queries/houseApiQueries";
import { API } from "aws-amplify";
import { FloatingHelperButton } from "../components";

const Root = styled.div`
  position: relative;
  overflow-y: auto;
`;
const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  background: rgba(255, 255, 255, 0.73);
`;

export const HouseWizard = () => {
  const [activeWizard, setActiveWizard] = useState<IWizardHouseState | null>(
    null
  );
  const {
    cognitoUser,
    userProfile,
    setNotFinishedRequiredHouseWizard,
    cleanNotFinishedRequiredHouseWizard
  } = useContextHospi();
  const [stepsOrder, setStepsOrder] = useState<T_WIZARD_HOUSE_STEP[] | null>(
    null
  );
  const { isLoading, watchPromise } = usePromisesLoader();
  const navigate = useHospiNavigate();
  const { t, i18n } = useTranslation("house");
  const params: { houseId?: string; wizardType?: TWizardHouseType } =
    useParams();

  const [userHasIntakeData, setUserHasIntakeData] = useState<boolean | null>(
    null
  );

  useEffect(() => {
    // check intake_data presence only on first load
    // because if change it later stepsOrder will be changed, and it could confuse user because intake step will disappear instantly
    if (userProfile?.user_id) {
      setUserHasIntakeData(Boolean(userProfile.intake_data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.user_id]);

  useEffect(() => {
    if (params.houseId && params.wizardType && userHasIntakeData !== null) {
      const houseWizardPromise = HouseAPI.getWizardState(
        params.houseId,
        params.wizardType
      );

      if (params.wizardType === "description") {
        setStepsOrder(WIZARD_HOUSE_DESCRIPTION_STEPS_ORDER);
      }
      if (params.wizardType === "publish") {
        setStepsOrder(
          userHasIntakeData
            ? WIZARD_HOUSE_PUBLISH_STEPS_ORDER.filter(
                (s) => s !== WIZARD_HOUSE_STEPS.PlanedIntake
              )
            : WIZARD_HOUSE_PUBLISH_STEPS_ORDER
        );
      }
      if (params.wizardType === "full") {
        setStepsOrder(WIZARD_HOUSE_FULL_STEPS_ORDER);
      }

      watchPromise(
        houseWizardPromise.then((wizard) => {
          setActiveWizard(wizard);
        })
      );

      return () => {
        API.cancel(houseWizardPromise);
      };
    }
  }, [
    params.houseId,
    params.wizardType,
    activeWizard?.house_id,
    activeWizard?.wizard_type,
    watchPromise,
    userHasIntakeData
  ]);

  const getStepTitle = useCallback(
    (stepName: T_WIZARD_HOUSE_STEP) => {
      return t(`wizard.steps.${stepName}.title`, {
        defaultValue: null
      });
    },
    [t]
  );
  const getStepDescription = useCallback(
    (stepName: T_WIZARD_HOUSE_STEP) => {
      return t(`wizard.steps.${stepName}.description`, {
        defaultValue: null
      });
    },
    [t]
  );

  const getOverviewStepTitle = useCallback(
    (stepName: T_WIZARD_HOUSE_STEP) => {
      return t(`wizard.steps.${stepName}.titleOverview`, {
        defaultValue: null
      });
    },
    [t]
  );

  const finishWizard = async () => {
    let result = await HouseAPI.finishWizard(
      activeWizard!.house_id!,
      activeWizard!.wizard_type!
    );
    invalidateWizardsStateShort();
    invalidateHouses(cognitoUser?.id!);

    return result;
  };

  return (
    <Root>
      {activeWizard && stepsOrder ? (
        <>
          {activeWizard.wizard_type === "description" ? (
            <Wizard<
              T_WIZARD_HOUSE_STEP,
              T_WIZARD_HOUSE_STEPS_PATH,
              IWizardHouseState,
              typeof WIZARD_HOUSE_STEPS_PATHS
            >
              key={activeWizard.wizard_type}
              wizardType="house"
              stepsOrder={stepsOrder}
              stepsPathsInState={WIZARD_HOUSE_STEPS_PATHS}
              stepsConfiguration={WIZARD_HOUSE_DESCRIPTION_STEPS_CONFIGURATION}
              defaultState={WIZARD_HOUSE_DEFAULT_STATE}
              getWizardState={() => {
                return HouseAPI.getWizardState(
                  activeWizard.house_id!,
                  activeWizard.wizard_type!
                );
              }}
              resetWizardState={() => {
                return HouseAPI.deleteWizard(
                  activeWizard.house_id!,
                  activeWizard.wizard_type!
                );
              }}
              finishWizard={finishWizard}
              getStepTitle={getStepTitle}
              getStepDescription={getStepDescription}
              getOverviewStepTitle={getOverviewStepTitle}
              additionalStepContent={<FloatingHelperButton />}
              onFinished={() => {
                const promise = HouseAPI.createWizard("publish").then(
                  (newWizard) => {
                    setNotFinishedRequiredHouseWizard(newWizard);
                    navigate(
                      generatePath(HOST_HOUSE_WIZARD_ROUTE, {
                        houseId: newWizard.house_id,
                        wizardType: newWizard.wizard_type,
                        lng: i18n.language
                      })
                    );
                    invalidateWizardsStateShort();
                  }
                );

                watchPromise(promise);
              }}
            />
          ) : null}
          {activeWizard.wizard_type === "publish" ? (
            <Wizard<
              T_WIZARD_HOUSE_STEP,
              T_WIZARD_HOUSE_STEPS_PATH,
              IWizardHouseState,
              typeof WIZARD_HOUSE_STEPS_PATHS
            >
              key={activeWizard.wizard_type}
              wizardType="house"
              stepsOrder={stepsOrder}
              stepsPathsInState={WIZARD_HOUSE_STEPS_PATHS}
              stepsConfiguration={WIZARD_HOUSE_DESCRIPTION_STEPS_CONFIGURATION}
              defaultState={WIZARD_HOUSE_DEFAULT_STATE}
              getWizardState={() => {
                return HouseAPI.getWizardState(
                  activeWizard.house_id!,
                  activeWizard.wizard_type!
                );
              }}
              resetWizardState={() => {
                return HouseAPI.deleteWizard(
                  activeWizard.house_id!,
                  activeWizard.wizard_type!
                );
              }}
              finishWizard={finishWizard}
              getStepTitle={getStepTitle}
              getStepDescription={getStepDescription}
              getOverviewStepTitle={getOverviewStepTitle}
              additionalStepContent={<FloatingHelperButton />}
              onFinished={() => {
                cleanNotFinishedRequiredHouseWizard();
                navigate(HOST_ROOMS_ROUTE);
              }}
            />
          ) : null}
          {activeWizard.wizard_type === "full" ? (
            <Wizard<
              T_WIZARD_HOUSE_STEP,
              T_WIZARD_HOUSE_STEPS_PATH,
              IWizardHouseState,
              typeof WIZARD_HOUSE_STEPS_PATHS
            >
              key={activeWizard.wizard_type}
              wizardType="house"
              stepsOrder={stepsOrder}
              stepsPathsInState={WIZARD_HOUSE_STEPS_PATHS}
              stepsConfiguration={WIZARD_HOUSE_DESCRIPTION_STEPS_CONFIGURATION}
              defaultState={WIZARD_HOUSE_DEFAULT_STATE}
              getWizardState={() => {
                return HouseAPI.getWizardState(
                  activeWizard.house_id!,
                  activeWizard.wizard_type!
                );
              }}
              resetWizardState={() => {
                return HouseAPI.deleteWizard(
                  activeWizard.house_id!,
                  activeWizard.wizard_type!
                );
              }}
              finishWizard={finishWizard}
              getStepTitle={getStepTitle}
              getStepDescription={getStepDescription}
              getOverviewStepTitle={getOverviewStepTitle}
              additionalStepContent={<FloatingHelperButton />}
              onFinished={() => {
                navigate(HOST_ROOMS_ROUTE);
              }}
            />
          ) : null}
        </>
      ) : null}
      {isLoading ? (
        <LoaderOverlay>
          <LoadingIndicator />
        </LoaderOverlay>
      ) : null}
    </Root>
  );
};
